import { useTranslation } from "react-i18next";

import { COMMON_NAMESPACE, useFormatters } from "@prostocheck/i18n";
import { cn } from "@prostocheck/shared";

import {
  PAYMENT_METHOD_LABEL_KEY,
  RECEIPT_TYPE_TITLE_KEY,
} from "./receipt-view.constants";
import { IReceiptViewProps } from "./receipt-view.models";

/**
 * Renders a settlement document the way it is printed on paper — the layout
 * users recognise from the register tape. Shared by the SPA and the landing
 * demo, so it takes a receipt and nothing else.
 */
export const ReceiptView = ({
  receipt,
  cashRegisterName,
  className,
}: IReceiptViewProps) => {
  const { t } = useTranslation(COMMON_NAMESPACE);
  const { formatDateTime, formatPrice } = useFormatters();

  return (
    <article
      className={cn(
        "mx-auto w-full max-w-sm rounded-card bg-surface p-6 font-mono text-sm shadow-card ring-1 ring-ink-900/8",
        className,
      )}
    >
      <header className="border-b border-dashed border-ink-900/20 pb-4 text-center">
        {cashRegisterName && (
          <p className="font-semibold text-ink-900">{cashRegisterName}</p>
        )}
        <p className="mt-1 text-xs text-ink-500">
          {t("receiptView.registerFiscalNumber", {
            fiscalNumber: receipt.cashRegisterFn,
          })}
        </p>
        <p className="mt-3 font-semibold text-ink-900">
          {t(RECEIPT_TYPE_TITLE_KEY[receipt.type])}
        </p>
      </header>

      <ul className="border-b border-dashed border-ink-900/20 py-4">
        {receipt.items.map(item => (
          <li key={item.name} className="mb-3 last:mb-0">
            <p className="text-ink-900">{item.name}</p>
            <p className="flex justify-between text-ink-500">
              <span>
                {item.quantity} × {formatPrice(item.price)}
              </span>
              <span className="text-ink-900">{formatPrice(item.total)}</span>
            </p>
          </li>
        ))}
      </ul>

      <dl className="border-b border-dashed border-ink-900/20 py-4">
        <div className="flex justify-between text-base font-semibold text-ink-900">
          <dt>{t("receiptView.total")}</dt>
          <dd>{formatPrice(receipt.total)}</dd>
        </div>
        <div className="mt-2 flex justify-between text-ink-500">
          <dt>{t(PAYMENT_METHOD_LABEL_KEY[receipt.paymentMethod])}</dt>
          <dd>{formatPrice(receipt.total)}</dd>
        </div>
        {receipt.vatTotal > 0 && (
          <div className="mt-2 flex justify-between text-ink-500">
            <dt>{t("receiptView.vatIncluded")}</dt>
            <dd>{formatPrice(receipt.vatTotal)}</dd>
          </div>
        )}
      </dl>

      <footer className="pt-4 text-center text-xs text-ink-500">
        <p>
          {t("receiptView.receiptFiscalNumber", {
            fiscalNumber: receipt.fiscalNumber,
          })}
        </p>
        <p className="mt-1">
          {t("receiptView.localNumber", { number: receipt.localNumber })}
        </p>
        <p className="mt-1">{formatDateTime(receipt.issuedAt)}</p>
        <p className="mt-3 font-semibold tracking-wide text-ink-700">
          {t("receiptView.fiscalReceipt")}
        </p>
      </footer>
    </article>
  );
};
